import * as Sentry from "@sentry/react";
import { useQuery } from "@tanstack/react-query";
import { Button, Card, message, Switch } from "antd";
import dayjs, { type Dayjs } from "dayjs";
import timezone from "dayjs/plugin/timezone";
import { Plus } from "lucide-react";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  ACTIVITY_EVENTS,
  EVENT_TYPE,
  ProgramService,
  type CalendarEvent,
} from "@fitness-app/data-models/domain/services/ProgramService";
import { getErrorMessage } from "@fitness-app/utils";

import {
  clientScheduledTasksActions,
  traineeActivitiesActions,
  traineeMeasurementsActions,
} from "@fitness-app/app-store";

import { CalendarDndContext } from "~/components/EventCalendar/CalendarDndContext";
import {
  getViewTitle,
  moveToDay,
  shiftViewDate,
  toDayKey,
  type CalendarView,
  type DayMenuFactory,
  type RenderEventOptions,
} from "~/components/EventCalendar/calendarModel";
import { CalendarToolbar } from "~/components/EventCalendar/CalendarToolbar";
import { CalendarViewSwitch } from "~/components/EventCalendar/CalendarViewSwitch";
import ModalForm from "~/components/ModalForm/ModalForm";
import { useIsPhone } from "~/hooks/useIsMobile";
import EditTraineeWeekSchedule from "~/modules/Trainee/TraineeProfile/TraineeFeatures/TraineeCalendar/components/EditTraineeWeekSchedule/EditTraineeWeekSchedule";
import EditTraineeWorkoutDay from "~/modules/Trainee/TraineeProfile/TraineeFeatures/TraineeCalendar/components/EditTraineeWorkoutDay/EditTraineeWorkoutDay";
import { useAddingProgramAutomationToClient } from "~/modules/Trainee/TraineeProfile/TraineeFeatures/TraineeCalendar/hooks/useAddingProgramAutomationToClient";
import { useCalendarEvents } from "~/modules/Trainee/TraineeProfile/TraineeFeatures/TraineeCalendar/hooks/useCalendarEvents";
import { useScheduledProgramsForRange } from "~/modules/Trainee/TraineeProfile/TraineeFeatures/TraineeCalendar/hooks/useScheduledProgramsForRange";
import { useTraineeExerciseRecordingsForRange } from "~/modules/Trainee/TraineeProfile/TraineeFeatures/TraineeMeasurements/TraineeVideoRecordings/useTraineeExerciseRecordings";
import AutomatedMessagesFormProvider, {
  useAutomatedMessagesFormContext,
} from "~/shared/providers/AutomatedMessagesFormProvider";
import { useAppDispatch, useAppSelector } from "~/store/initializeStore";
import AutomationScheduleSelectorForm from "./components/AutomationScheduleSelectorForm/AutomationScheduleSelectorForm";
import { CalendarEventChip, CalendarEventChipPreview } from "./components/EventCalendar/CalendarEventChip";
import { getEventKey, groupEventsByDay } from "./components/EventCalendar/traineeCalendarEvents";
import { NutritionDaySummary as NutritionDaySummaryModal } from "./components/Nutrition/NutritionDaySummary";
import { useTraineeId, withTraineeStore } from "~/modules/Trainee/TraineeProfile/scope";

dayjs.extend(timezone);

const PHONE_VIEWS: CalendarView[] = ["agenda", "month"];
const DESKTOP_VIEWS: CalendarView[] = ["month", "week", "agenda"];

const TraineeCalendar = () => {
  // TRE-2076: na telefonie agenda zamiast siatki miesiąca i skrócony pasek narzędzi.
  const isPhone = useIsPhone();
  const { t } = useTranslation(["trainees", "automation"]);
  const [currentDate, setCurrentDate] = useState<Date>(new Date());
  const [view, setView] = useState<CalendarView>(isPhone ? "agenda" : "month");
  const [onlyCurrentPlanWorkouts, setOnlyCurrentPlanWorkouts] = useState(false);
  const dispatch = useAppDispatch();
  const scopedTraineeId = useTraineeId();
  const { selectedProgram, programDetails } = useAppSelector((state) => state.traineeProgram);
  const { measurementsForDateRange } = useAppSelector((state) => state.traineeMeasurements);
  const { profile } = useAppSelector((state) => state.trainee);
  const clientTasks = useAppSelector((state) => state.clientTasks.list);
  const traineeWorkoutActivities = useAppSelector((state) => state.traineeActivities.activities);
  const {
    showAutomationScheduleForm,
    toggleAutomationScheduleForm,
    addingAutomationToClient,
    handleAddingAutomationToClient,
  } = useAddingProgramAutomationToClient(profile?.id || "", currentDate);
  const { addNewMessage } = useAutomatedMessagesFormContext();

  const traineeId = profile?.id;
  const currentMonth = dayjs(currentDate).get("month");
  const currentYear = dayjs(currentDate).get("year");

  const activeView = isPhone && view === "week" ? "month" : view;

  // Tydzień na przełomie miesięcy pokazuje dni z sąsiedniego miesiąca, więc zakres to suma miesiąca
  // i tygodnia. Aktywności, pomiary i zadania Redux i tak pobiera z zapasem miesiąca w obie strony.
  const range = useMemo(() => {
    const month = { start: dayjs(currentDate).startOf("month"), end: dayjs(currentDate).endOf("month") };
    if (activeView !== "week") {
      return month;
    }
    const weekStart = dayjs(currentDate).startOf("isoWeek");
    const weekEnd = dayjs(currentDate).endOf("isoWeek");
    return {
      start: weekStart.isBefore(month.start) ? weekStart : month.start,
      end: weekEnd.isAfter(month.end) ? weekEnd : month.end,
    };
  }, [currentDate, activeView]);
  const { data: calendarEvents } = useCalendarEvents(profile?.id || "", range);
  const { scheduledPrograms } = useScheduledProgramsForRange(traineeId, range);
  const { byWorkoutExerciseId } = useTraineeExerciseRecordingsForRange(profile?.id || "", range);

  const { data: calendarNutritionStatistics = [] } = useQuery(
    ["trainee_calendar_data", traineeId, currentYear, currentMonth],
    {
      enabled: Boolean(traineeId),
      refetchOnWindowFocus: true,
      queryFn: async () => {
        if (!traineeId) {
          return [];
        }

        const selectedDate = dayjs(currentDate);

        const nutritionSummaryPromise = dispatch(
          traineeMeasurementsActions.fetchNutritionSummaryForRange({
            dateStart: selectedDate.subtract(1, "month").startOf("month").toDate(),
            dateEnd: selectedDate.add(1, "month").endOf("month").subtract(1, "day").toDate(),
            traineeId,
          }),
        )
          .unwrap()
          .then((res) => res.map((item) => item.data).flat())
          .catch((e) => {
            Sentry.captureException(e, { data: { action: "fetchNutritionSummaryForRange in trainee calendar" } });
            return [];
          });

        await Promise.all([
          dispatch(traineeMeasurementsActions.fetchMeasurementsForRange({ traineeId, selectedDate: currentDate })),
          dispatch(clientScheduledTasksActions.fetchClientTasks({ traineeId, selectedDate })),
          dispatch(traineeActivitiesActions.fetchTraineeActivitiesForRange({ traineeId, selectedDate })),
        ]);

        return nutritionSummaryPromise;
      },
    },
  );

  const events = ProgramService.getWorkoutsForDateRange([range.start, range.end], selectedProgram, {
    workouts: programDetails?.workouts || [],
    archivedWorkouts: programDetails?.archivedWorkouts || [],
    clientNotifications: [],
    calendarEvents: calendarEvents || [],
    clientActivities: traineeWorkoutActivities,
    workoutsOnlyForCurrentPlan: onlyCurrentPlanWorkouts,
    nutritionStatistics: calendarNutritionStatistics,
    clientTasks,
    measurements: measurementsForDateRange,
    exerciseRecordings: byWorkoutExerciseId,
  });

  // TRE-1826 — treningi z planów zaplanowanych. Aktywny plan ma pierwszeństwo: gdy jego
  // zakres nachodzi na zaplanowany, w kalendarzu zostaje dzień, który trenerka może
  // edytować.
  const scheduledWorkoutEvents = useMemo(() => {
    if (onlyCurrentPlanWorkouts || !scheduledPrograms.length) {
      return [];
    }

    const datesFromActivePlan = new Set(
      events
        .filter((event) => event.type === EVENT_TYPE.WORKOUT)
        .map((event) => dayjs(event.start).format("YYYY-MM-DD")),
    );

    return ProgramService.getPreviewWorkoutsForDateRange([range.start, range.end], scheduledPrograms).filter(
      // Marker startu planu przechodzi zawsze — dedup dotyczy wyłącznie treningów.
      (event) => event.type !== EVENT_TYPE.WORKOUT || !datesFromActivePlan.has(dayjs(event.start).format("YYYY-MM-DD")),
    );
  }, [events, onlyCurrentPlanWorkouts, range.end, range.start, scheduledPrograms]);

  const eventsByDay = useMemo(
    () => groupEventsByDay([...events, ...scheduledWorkoutEvents]),
    [events, scheduledWorkoutEvents],
  );

  const onEventClick = useCallback(
    (event: CalendarEvent) => {
      // TRE-1826 — dzień z planu zaplanowanego: `setCurrentDayWorkout` zapisałoby go jako
      // aktywność przypiętą do planu AKTYWNEGO, więc edycja stąd jest wyłączona. Rozpiskę
      // zaplanowanego planu zmienia się na jego własnym stepperze.
      if (event.type === EVENT_TYPE.WORKOUT && event.metadata.readOnly) {
        return;
      }
      if (event.type === EVENT_TYPE.WORKOUT) {
        dispatch(
          traineeActivitiesActions.setCurrentDayWorkout({
            workout: event.data,
            date: dayjs(event.start).format("YYYY-MM-DD"),
            source: event.metadata.source,
            id: event.metadata.id ?? null,
            status: event.metadata.status ?? null,
            recordings: event.metadata.requiredRecordings ?? null,
          }),
        );
        dispatch(traineeActivitiesActions.setWorkoutEditModalShown());
      }
    },
    [dispatch],
  );

  const moveEvent = useCallback(
    async (event: CalendarEvent, newDate: Date) => {
      if (event.type === ACTIVITY_EVENTS.WORKOUT) {
        try {
          // Thunk zapisuje harmonogram do programu z Reduxa po `await` - blokada trzyma tę klientkę
          // w Reduxie, nawet jeśli w trakcie zapisu przełączysz kartę.
          await withTraineeStore(scopedTraineeId, () =>
            dispatch(traineeActivitiesActions.dragEventToSpecifiedDay({ newDate, event })).unwrap(),
          );
        } catch (e) {
          void message.warning(getErrorMessage(e));
        }
      }

      if (event.type === EVENT_TYPE.AUTOMATION_TASK) {
        if (newDate < new Date()) {
          void message.warning("Nie można przesunąć wysyłki na datę w przeszłości");
          return;
        }

        void dispatch(
          clientScheduledTasksActions.updateClientTask({
            id: event.data.id,
            task: { sendDate: dayjs(newDate).toISOString(), sendAt: dayjs(newDate).unix() },
            isOptimisticUpdate: true,
          }),
        );
      }
    },
    [dispatch, scopedTraineeId],
  );

  const onDrop = useCallback(
    (data: Record<string, unknown>, dayKey: string) => {
      const event = data.event as CalendarEvent | undefined;
      if (!event || toDayKey(event.start) === dayKey) {
        return;
      }
      // Wiadomość liczymy od `sendDate`, bo `start` zadania to początek dnia i przesunięcie od
      // niego ustawiało wysyłkę na północ.
      void moveEvent(
        event,
        moveToDay(event.type === EVENT_TYPE.AUTOMATION_TASK ? event.data.sendDate : event.start, dayKey).toDate(),
      );
    },
    [moveEvent],
  );

  const renderEvent = useCallback(
    (event: CalendarEvent, options: RenderEventOptions) => (
      <CalendarEventChip event={event} {...options} onClick={onEventClick} />
    ),
    [onEventClick],
  );

  const dayMenu = useMemo<DayMenuFactory | undefined>(() => {
    if (!selectedProgram?.id) {
      return undefined;
    }
    return (day: Dayjs) => [
      {
        key: "workout",
        label: "Dodaj trening",
        onClick: () => {
          dispatch(
            traineeActivitiesActions.setCurrentDayWorkout({
              date: day.format("YYYY-MM-DD"),
              workout: null,
              status: null,
            }),
          );
          dispatch(traineeActivitiesActions.setWorkoutEditModalShown());
        },
      },
      {
        key: "schedule",
        label: "Zmień harmonogram tygodnia",
        onClick: () => {
          setCurrentDate(day.toDate());
          dispatch(traineeActivitiesActions.setModalEditWeekScheduleShown());
        },
      },
      {
        key: "message",
        label: "Dodaj automatyczną wiadomość",
        onClick: () => {
          setCurrentDate(day.toDate());
          addNewMessage(day);
        },
      },
    ];
  }, [selectedProgram?.id, dispatch, addNewMessage]);

  const viewDate = dayjs(currentDate);

  return (
    <Card styles={{ body: { padding: isPhone ? 12 : 20 } }}>
      <div className="flex flex-col gap-4">
        <CalendarToolbar
          title={getViewTitle(activeView, viewDate)}
          view={activeView}
          views={isPhone ? PHONE_VIEWS : DESKTOP_VIEWS}
          onViewChange={setView}
          onPrev={() => setCurrentDate((prev) => shiftViewDate(activeView, prev, -1))}
          onNext={() => setCurrentDate((prev) => shiftViewDate(activeView, prev, 1))}
          onToday={() => setCurrentDate(new Date())}
          extra={
            <>
              <label className="flex cursor-pointer items-center gap-2 text-sm text-gray-600">
                <Switch
                  size="small"
                  checked={onlyCurrentPlanWorkouts}
                  onChange={() => setOnlyCurrentPlanWorkouts((prev) => !prev)}
                />
                Tylko obecny plan
              </label>
              <Button
                type="primary"
                icon={<Plus size={16} className="align-[-3px]" />}
                onClick={() => toggleAutomationScheduleForm(true)}
              >
                {t("automation:button.addAutomation")}
              </Button>
            </>
          }
        />
        <CalendarDndContext
          onDrop={onDrop}
          renderOverlay={(data) => <CalendarEventChipPreview event={data.event as CalendarEvent} />}
        >
          <CalendarViewSwitch
            view={activeView}
            date={viewDate}
            eventsByDay={eventsByDay}
            getEventKey={getEventKey}
            renderEvent={renderEvent}
            dayMenu={dayMenu}
          />
        </CalendarDndContext>
      </div>
      <EditTraineeWeekSchedule currentDate={currentDate} />
      <NutritionDaySummaryModal />
      <EditTraineeWorkoutDay />
      <ModalForm
        title="Dodaj automatyzacje do klienta"
        open={showAutomationScheduleForm}
        onCancel={() => toggleAutomationScheduleForm(false)}
        loading={!!addingAutomationToClient}
      >
        <AutomationScheduleSelectorForm onSubmit={handleAddingAutomationToClient} />
      </ModalForm>
    </Card>
  );
};

const WrappedTraineeCalendar = () => (
  <AutomatedMessagesFormProvider>
    <TraineeCalendar />
  </AutomatedMessagesFormProvider>
);

export default WrappedTraineeCalendar;
